import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  IEcommerceProductHierarchy,
  IEcommerceProductHierarchyItem,
  IEcommerceSubBrandHierarchy
} from 'src/models/ecommerce';
import useEcommerce from 'src/services/useEcommerce';
import { reducerUpdateAdvanceSearch } from 'src/features/advanceSearchEcommerce';
import FilterProductList from './FilterProductList';

export default function FilterProduct() {
  const theme = useTheme();
  const { selectedOriginalToPost, selectedSubBrand, selectedBrand } =
    useAppSelector((state) => state.storeAdvancedSearchEcommerce);
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dispatch = useAppDispatch();

  const projectId = useAppSelector((state) => state.users.project.id);
  const advanceSearchEcommerce = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );
  const { useGetEcommerceStreamCommentingToProduct } = useEcommerce();

  const {
    data: streamsCommentingToPostData,
    loading: loadingStreamsCommentingToPost
  } = useGetEcommerceStreamCommentingToProduct({
    skip: !open,
    variables: {
      projectId: projectId
    },
    fetchPolicy: 'network-only'
  });

  const [
    selectedCommentingToProductsLocal,
    setSelectedCommentingToProductsLocal
  ] = useState<IEcommerceProductHierarchyItem[]>(selectedOriginalToPost);

  const [selectedSubBrandLocal, setSelectedSubBrandLocal] =
    useState<IEcommerceSubBrandHierarchy[]>(selectedSubBrand);

  const [selectedBrandLocal, setSelectedBrandLocal] =
    useState<IEcommerceProductHierarchy[]>(selectedBrand);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleApply = () => {
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearchEcommerce,
        replyToOriginalId: selectedCommentingToProductsLocal.map(
          (stream) => stream.originalId
        ),
        selectedOriginalToPost: selectedCommentingToProductsLocal,
        selectedBrand: selectedBrandLocal,
        selectedSubBrand: selectedSubBrandLocal
      })
    );
    handleClose();
  };

  useEffect(() => {
    setSelectedCommentingToProductsLocal(selectedOriginalToPost);
  }, [selectedOriginalToPost]);

  return (
    <>
      <Stack maxWidth="400px" width="100%" spacing={0.5}>
        <Typography variant="h5" fontWeight={600}>
          Product
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleOpen}
          sx={{
            color: 'text.primary',
            backgroundColor: 'white',
            borderRadius: theme.spacing(1),
            maxWidth: '400px',
            width: '100%',
            minHeight: '36px',
            borderColor: (theme) =>
              open ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
              backgroundColor: 'white'
            },
            '& .MuiButton-startIcon': { marginRight: 'auto' },
            '& .buttonText': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textAlign: 'left',
              flex: '1'
            },
            padding: '4px 8px'
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
                marginRight: '2px'
              }}
            />
          }
        >
          {selectedOriginalToPost.length <= 0 ? (
            <span className="buttonText">All Product</span>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.5,
                // maxWidth: '320px',
                // maxWidth: '500px',
                width: '100%',
                overflow: 'scroll',
                scrollbarWidth: 'none'
              }}
            >
              {selectedOriginalToPost.map((group, index) => (
                <Chip
                  key={`${group}-${index}`}
                  variant="outlined"
                  sx={{
                    maxWidth:
                      selectedOriginalToPost.length > 1 ? '150px' : '300px',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }
                  }}
                  label={group.name}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Button>
      </Stack>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: theme.spacing(2), maxWidth: '600px', width: '100%' }}>
          <Typography variant="h4">Product</Typography>
          <Box
            sx={{ mt: theme.spacing(1), maxHeight: '300px', overflow: 'auto' }}
          >
            <FilterProductList
              data={
                streamsCommentingToPostData?.ecommerceProductManagement_getProductHierarchy ||
                []
              }
              loading={loadingStreamsCommentingToPost}
              selectedProducts={selectedCommentingToProductsLocal}
              onSelectedProductsChange={(
                products: IEcommerceProductHierarchyItem[]
              ) => {
                setSelectedCommentingToProductsLocal(products);
              }}
              setSelectedBrand={setSelectedBrandLocal}
              setSelectedSubBrand={setSelectedSubBrandLocal}
            />
          </Box>
          <Stack pt={2} alignItems="end">
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
